/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import {
  RuleActionValues,
  RuleTypes,
  conditionFactMapping,
} from '@/utilities/rules';

const STORE_DROPOFF_CRITERIA_TYPES = {
  BRAND: 'brand',
  STORE_TAG: 'storeTag',
};

const buildStoreDropOffActionValue = (eventData = {}) => {
  if (eventData.enabled === RuleActionValues.FALSE) {
    return {
      enabled: false,
    };
  }

  const values = eventData.criteriaValues || [];
  const criteria = {};

  if (eventData.criteriaType === STORE_DROPOFF_CRITERIA_TYPES.BRAND && values.length) {
    criteria.subBrandCompanyIds = { include: values };
  }

  if (eventData.criteriaType === STORE_DROPOFF_CRITERIA_TYPES.STORE_TAG && values.length) {
    criteria.tags = { include: values };
  }

  return {
    enabled: eventData.enabled !== RuleActionValues.FALSE,
    ...(Object.keys(criteria).length && { criteria }),
  };
};

export default function generatePayload({
  ruleType, companyId, name, conditions,
  eventData, copyData, priority = 1, isDefault,
  enabled = true,
}) {
  const basePayload = {
    companyId,
    ruleType,
    ruleDescription: name,
    enabled,
    ...isDefault && { isDefault: true },
    rule: {
      conditions: {
        all: conditions.map(({ path, ...rest }) => {
          const fact = rest.fact ?? conditionFactMapping[path];
          return {
            ...rest,
            ...(fact && { fact }),
            ...(path != null && fact !== 'returnMethod' && { path }),
          };
        }),
      },
      event: {
      },
    },
  };

  if (ruleType === RuleTypes.NON_RETURNABLE_ITEM) {
    basePayload.rule.event = {
      type: 'nonReturnableItem',
      params: {
        actionId: 'not_returnable',
        actionValue: {
          isNotReturnable: true,
          ...(copyData?.customCopy && { customCopy: copyData.customCopy }),
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.RETURN_WINDOWS) {
    basePayload.rule.event = {
      type: 'withinReturnWindow',
      params: {
        actionId: 'return_window',
        actionValue: {
          returnWindow: {
            days: eventData.days,
            from: eventData.from,
          },
          ...(copyData?.customCopy && { customCopy: copyData.customCopy }),
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.RETURN_ADDRESS) {
    basePayload.rule.event = {
      type: 'returnAddress',
      params: {
        actionId: 'return_address',
        actionValue: {
          locationId: eventData.locationId,
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.ORDER_EXCEPTION) {
    basePayload.ruleDescription = name || 'Order exception rule';
    basePayload.rule.event = {
      type: 'exceptionAlwaysReturnableItem',
      params: {
        actionId: 'exception_always_returnable',
        actionValue: {
          alwaysReturnable: true,
        },
      },
    };
  }

  if (ruleType === RuleTypes.CARRIER_RULES) {
    basePayload.rule.event = {
      type: 'returnCarrier',
      params: {
        actionId: 'return_carrier',
        actionValue: {
          returnCarrier: {
            shouldUse: eventData.shouldUse === RuleActionValues.TRUE,
            carrier: eventData.carrier,
            serviceType: eventData.serviceType,
          },
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.REFUND_TRIGGERS) {
    basePayload.rule.event = {
      type: 'refundTrigger',
      params: {
        actionId: 'refund_trigger',
        actionValue: {
          refundTrigger: {
            eventType: eventData.eventType,
          },
          ...(copyData?.customCopy && { customCopy: copyData.customCopy }),
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.REFUND_FEES) {
    basePayload.rule.event = {
      type: 'refundFee',
      params: {
        actionId: 'refund_fee',
        actionValue: {
          refundFee: {
            amount: eventData.amount,
            name: eventData.name,
            currency: eventData.currency,
          },
          ...(copyData?.customCopy && { customCopy: copyData.customCopy }),
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.RETURNLESS_ITEM) {
    basePayload.rule.event = {
      type: 'returnlessItem',
      params: {
        actionId: 'returnless',
        actionValue: {
          returnless: true,
          ...(copyData?.customCopy && { customCopy: copyData.customCopy }),
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.REFUND_METHOD) {
    basePayload.rule.event = {
      type: 'refundMethod',
      params: {
        actionId: 'refund_method',
        actionValue: {
          refundMethods: eventData.refundMethods,
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.HOME_PICKUP) {
    basePayload.rule.event = {
      type: 'homePickUp',
      params: {
        actionId: 'homePickUp',
        actionValue: {
          homePickUp: {
            enabled: true,
          },
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  if (ruleType === RuleTypes.STORE_DROPOFF) {
    basePayload.rule.event = {
      type: 'storeDropOff',
      params: {
        actionId: 'storeDropOff',
        actionValue: {
          storeDropOff: buildStoreDropOffActionValue(eventData),
        },
      },
    };
    basePayload.rule.priority = priority;
  }

  return basePayload;
}
